import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Dictionary } from "lodash";
import { BehaviorSubject, Observable } from "rxjs";
import { AuthService } from "src/app/core/service/auth.service";
import { SnackbarService } from "src/app/core/service/snackbar.service";
import { SubscriptionService } from "src/app/core/service/subscription.service";
import { CategoriaTO } from "src/app/data/schema/Courses/CategoriaTO";
import { ContactUsTO } from "src/app/data/schema/Subscriptions/ContactUsTO";
import { UserCustomFieldDTO } from "src/app/data/schema/Subscriptions/UserCustomFieldDTO";
import { UserCustomFieldTypeDTO } from "src/app/data/schema/Subscriptions/UserCustomFieldTypeDTO";
import { PatchUserAccountTO } from "src/app/data/schema/User/PatchUserAccountTO";
import { PatchUseInterestsTO } from "src/app/data/schema/User/PatchUserInterestsTO";
import { PatchUserPreferencesTO } from "src/app/data/schema/User/PatchUserPreferencesTO";
import { PatchUserSkillsDTO } from "src/app/data/schema/User/PatchUserSkillsDTO";
import { UsuarioTO } from "src/app/data/schema/User/UsuarioTO";
import { ApiCoursesService } from "src/app/data/service/courses/api-courses.service";
import { ApiSubscriptionsService } from "src/app/data/service/subscriptions/api-subscriptions.service";
import { ApiUserService } from "src/app/data/service/user/api-user.service";

@Injectable({
    providedIn: "root"
})
export class ProfileService {
    private loading: BehaviorSubject<boolean>;
    private user: BehaviorSubject<UsuarioTO>;
    private categories: BehaviorSubject<CategoriaTO[]>;
    private skills: BehaviorSubject<string[]>;
    private clearEmailForm: BehaviorSubject<boolean>;
    public userCustomFields: BehaviorSubject<UserCustomFieldDTO[]>;

    constructor(
        private translate: TranslateService,
        private authService: AuthService,
        private userService: ApiUserService,
        private courseService: ApiCoursesService,
        private _apiSubscriptionsService: ApiSubscriptionsService,
        private snackBar: SnackbarService,
        private subscriptionService: SubscriptionService
    ) {
        this.loading = new BehaviorSubject<boolean>(false);
        this.user = new BehaviorSubject<UsuarioTO>(undefined);
        this.categories = new BehaviorSubject<CategoriaTO[]>(undefined);
        this.skills = new BehaviorSubject<string[]>(undefined);
        this.clearEmailForm = new BehaviorSubject<boolean>(false);
        this.userCustomFields = new BehaviorSubject<UserCustomFieldDTO[]>(null);

        this.subscriptionService
            .currentSubscriptionAsync()
            .subscribe((sub) => this.userCustomFields.next(sub.settings.userCustomFields ?? null));
    }

    public clear() {
        this.loading.next(false);
        this.user.next(undefined);
        this.categories.next(undefined);
        this.skills.next(undefined);
        this.clearEmailForm.next(false);
    }

    public userCustomFieldsAsync = (): Observable<UserCustomFieldDTO[]> => this.userCustomFields.asObservable();

    public getLoadingAsync = (): Observable<boolean> => this.loading.asObservable();

    public getUserAsync = (): Observable<UsuarioTO> => this.user.asObservable();

    public getCategoriesAsync = (): Observable<CategoriaTO[]> => this.categories.asObservable();

    public getSkillsAsync = (): Observable<string[]> => this.skills.asObservable();

    public getClearEmailFormAsync = (): Observable<boolean> => this.clearEmailForm.asObservable();

    public getLoggedUser(): void {
        this.loading.next(true);

        const userId = this.authService.getClaimsUserId();
        this.userService.getUserById(userId).subscribe(
            (user) => {
                this.user.next(user);
                this.loading.next(false);
            },
            (error) => {
                this.snackBar.showHttpMessage(error.status, error.message);
                this.loading.next(false);
            }
        );
    }

    public fetchCategories(): void {
        this.loading.next(true);

        this.courseService.getCategorias().subscribe(
            (data: CategoriaTO[]) => {
                this.categories.next(data);
                this.loading.next(false);
            },
            (error) => {
                this.snackBar.showHttpMessage(error.status, error.message);
                this.loading.next(false);
            }
        );
    }

    public fetchSkills(): void {
        this.loading.next(true);

        this.courseService.listSkills().subscribe(
            (data: string[]) => {
                this.skills.next(data);
                this.loading.next(false);
            },
            (error) => {
                this.snackBar.showHttpMessage(error.status, error.message);
                this.loading.next(false);
            }
        );
    }

    public updateUserAccount(cardData: PatchUserAccountTO, filesToUpload?: Dictionary<HTMLInputElement>): void {
        this.loading.next(true);

        const filefieldName = this.userCustomFields?.value
            ?.filter((x) => x.type == UserCustomFieldTypeDTO.File)
            ?.map((x) => x.fieldName);

        this.userService.updateUserAccount(cardData).subscribe(
            (_) => {
                if (filesToUpload) {
                    filefieldName?.forEach((field) => {
                        const file = filesToUpload[field];
                        const last = filefieldName.indexOf(field) == filefieldName.length - 1;
                        if (file) this.uploadFileCustomField(this.user.value.id, file, field, last);
                    });
                }

                this.loading.next(false);
                this.translate.get("MESSAGES.UPDATED").subscribe((res) => {
                    this.snackBar.showMessage(res);
                });
            },
            (error) => {
                this.snackBar.showHttpMessage(error.status, error.message);
                this.loading.next(false);
            }
        );
    }

    public uploadFileCustomField(userId: string, input: HTMLInputElement, fieldKey: string, lastField: boolean): void {
        if (input.files.length > 0)
            this.userService.uploadFileCustomField(userId, input.files[0], fieldKey).subscribe(() => {
                if (lastField) this.getLoggedUser();
            });
    }

    public updateUserPreferences(cardData: PatchUserPreferencesTO): void {
        this.loading.next(true);

        this.userService.updateUserPreferences(cardData).subscribe(
            (_) => {
                this.loading.next(false);
                this.translate.get("MESSAGES.UPDATED").subscribe((res) => {
                    this.snackBar.showMessage(res);
                });

                localStorage.setItem("locale", cardData.idioma);

                if (cardData.idioma) this.translate.use(cardData.idioma);
                else this.translate.use("pt-BR");
            },
            (error) => {
                this.snackBar.showHttpMessage(error.status, error.message);
                this.loading.next(false);
            }
        );
    }

    public updateUserInterests(cardData: PatchUseInterestsTO): void {
        this.loading.next(true);

        this.userService.updateUserInterests(cardData).subscribe(
            (_) => {
                this.loading.next(false);
                this.translate.get("MESSAGES.UPDATED").subscribe((res) => {
                    this.snackBar.showMessage(res);
                });
            },
            (error) => {
                this.snackBar.showHttpMessage(error.status, error.message);
                this.loading.next(false);
            }
        );
    }

    public updateUserSkills(cardData: PatchUserSkillsDTO): void {
        this.loading.next(true);

        this.userService.updateUserSkills(cardData).subscribe(
            (_) => {
                this.loading.next(false);
                this.translate.get("MESSAGES.UPDATED").subscribe((res) => {
                    this.snackBar.showMessage(res);
                });
            },
            (error) => {
                this.snackBar.showHttpMessage(error.status, error.message);
                this.loading.next(false);
            }
        );
    }

    public requestUserEmailUpdate(newEmail: string): void {
        this.loading.next(true);

        this.userService.RequestEmailChange(this.authService.getClaimsUserId(), newEmail).subscribe(
            (_) => {
                this.loading.next(false);
                this.translate.get("CHANGE_EMAIL.REQUESTED").subscribe((res) => {
                    this.snackBar.showMessage(res);
                });
            },
            (error) => {
                if (error.error.includes("Email Already in use")) {
                    this.translate
                        .get("CHANGE_EMAIL.EMAIL_ALREADY_IN_USE")
                        .subscribe((msg) => this.snackBar.showMessage(msg, 3000));
                } else {
                    this.snackBar.showHttpMessage(error.status, error.message);
                }
                this.loading.next(false);
            }
        );
    }

    public updateUserPassword(oldPassWord: string, newPassword: string): void {
        this.loading.next(true);

        this.authService.changePasswordLogged(oldPassWord, newPassword).subscribe(
            (_) => {
                this.loading.next(false);
                this.translate.get("MESSAGES.UPDATED").subscribe((res) => {
                    this.snackBar.showMessage(res);
                });
            },
            (error) => {
                this.translate.get("MESSAGES.REQUIRED").subscribe((res) => {
                    this.snackBar.showMessage(res);
                });
                this.loading.next(false);
            }
        );
    }

    public sendSupportEmail(cardData: ContactUsTO): void {
        this.loading.next(true);

        const userEmail = this.user.value.email;

        this._apiSubscriptionsService
            .ContactUs(cardData, this.user.value.id, `${this.user.value.nome} ${this.user.value.sobrenome}`, userEmail)
            .subscribe(
                (_) => {
                    this.loading.next(false);
                    this.clearEmailForm.next(true);
                    this.translate.get("MESSAGES.UPDATED").subscribe((res) => {
                        this.snackBar.showMessage(res);
                    });
                },
                (error) => {
                    this.snackBar.showHttpMessage(error.status, error.message);
                    this.loading.next(false);
                    this.clearEmailForm.next(false);
                }
            );
    }
}
